import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

// XHR-based POST. Fetch is monkey-patched by the Emergent platform script
// (it eagerly reads response bodies for logging) which causes
// "body stream already read" / "Response body is already used" errors when
// our code tries to read 4xx responses. XMLHttpRequest is not affected.
function xhrJson(method, url, body, headers = {}) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      let data = {};
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; }
      catch { data = { detail: xhr.responseText }; }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, data: { detail: 'Network error' } });
    xhr.send(body ? JSON.stringify(body) : null);
  });
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  // Fetch profile via our backend (service-role) — avoids RLS edge cases.
  const fetchProfile = useCallback(async (token) => {
    if (!token) return;
    const { ok, data } = await xhrJson('GET', `${API}/api/auth/profile`, null, {
      Authorization: `Bearer ${token}`,
    });
    if (ok && data?.profile) setProfile(data.profile);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (session?.access_token) await fetchProfile(session.access_token);
  }, [session, fetchProfile]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.access_token) fetchProfile(session.access_token);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.access_token) fetchProfile(session.access_token);
    });

    return () => subscription?.unsubscribe();
  }, [fetchProfile]);

  // Sign up via our backend (avoids supabase-js / Emergent fetch-patch issues
  // by using XMLHttpRequest under the hood). Returns a structured error:
  //   { code: 'account_exists' | 'email_not_confirmed' | ... , message }
  const signUp = async (email, password) => {
    const { ok, status, data } = await xhrJson('POST', `${API}/api/auth/signup`, { email, password });
    if (!ok) {
      // Backend now returns detail={code, message} for well-known cases.
      const detail = data?.detail;
      if (detail && typeof detail === 'object') {
        return { data: null, error: { code: detail.code, message: detail.message } };
      }
      return { data: null, error: { message: detail || data?.message || `Sign up failed (${status})` } };
    }
    if (data?.access_token && data?.refresh_token) {
      const { data: setData } = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });
      if (setData?.session) {
        setSession(setData.session);
        setUser(setData.session.user);
        fetchProfile(setData.session.access_token);
      }
    }
    return { data, error: null };
  };

  const signIn = async (email, password) => {
    const { ok, status, data } = await xhrJson('POST', `${API}/api/auth/signin`, { email, password });
    if (!ok) {
      const detail = data?.detail;
      if (detail && typeof detail === 'object') {
        return { data: null, error: { code: detail.code, message: detail.message } };
      }
      return { data: null, error: { message: detail || data?.message || `Sign in failed (${status})` } };
    }
    const { data: setData, error: setErr } = await supabase.auth.setSession({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    });
    if (setErr) {
      return { data: null, error: { message: setErr.message } };
    }
    if (setData?.session) {
      setSession(setData.session);
      setUser(setData.session.user);
      // Fetch profile and RETURN it so callers can route by role without
      // needing a second effect tick (fixes manager login routing).
      const pRes = await xhrJson('GET', `${API}/api/auth/profile`, null, {
        Authorization: `Bearer ${setData.session.access_token}`,
      });
      const fetchedProfile = pRes?.data?.profile || null;
      if (fetchedProfile) setProfile(fetchedProfile);
      return { data: { ...setData, profile: fetchedProfile }, error: null };
    }
    return { data: setData, error: null };
  };

  const resendConfirmation = async (email) => {
    const { ok, data } = await xhrJson(
      'POST',
      `${API}/api/auth/resend-confirmation`,
      { email, password: 'x' },
    );
    if (!ok) return { error: { message: data?.detail || 'No se pudo reenviar' } };
    return { data };
  };

  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/cliente` },
    });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    // Clear any stale in-memory state so the next login starts clean.
    setProfile(null);
    setSession(null);
    setUser(null);
    try {
      // Defensive: clear persisted session keys that could linger after
      // a previous deploy under a different Supabase project ref.
      Object.keys(localStorage)
        .filter(k => k.startsWith('sb-') && k.endsWith('-auth-token'))
        .forEach(k => localStorage.removeItem(k));
    } catch { /* noop */ }
    return { error };
  };

  // Update profile fields via backend (service-role bypasses RLS edge cases).
  const updateProfile = async (updates) => {
    if (!user || !session?.access_token) return { error: { message: 'Not authenticated' } };
    const payload = {};
    if ('full_name' in updates) payload.full_name = updates.full_name;
    if ('avatar_url' in updates) payload.avatar_url = updates.avatar_url;
    const { ok, data } = await xhrJson('PATCH', `${API}/api/auth/profile`, payload, {
      Authorization: `Bearer ${session.access_token}`,
    });
    if (!ok) return { error: { message: data?.detail || 'Update failed' } };
    if (data?.profile) setProfile(data.profile);
    return { data: data?.profile };
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      loading,
      isLoggedIn: !!user,
      signUp,
      signIn,
      signInWithGoogle,
      signOut,
      updateProfile,
      refreshProfile,
      resendConfirmation,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
